import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { api, ApiError } from './api';
import type { PortfolioResponse } from './types';

interface PortfolioState {
  data: PortfolioResponse | null;
  loading: boolean;
  error: ApiError | null;
  reload: () => void;
}

const Ctx = createContext<PortfolioState | null>(null);

/**
 * The portfolio is fetched ONCE and shared.
 *
 * Both screens need the full list: the portfolio renders it, and the detail
 * header's stepper and facility switcher need the same filtered set to move
 * through. Fetching per-screen would make the stepper's "4 of 37" disagree with
 * the list the user just came from.
 */
export function PortfolioProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const [data, setData] = useState<PortfolioResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  // The provider is mounted above the MSAL templates so the chrome can read
  // projection freshness, which means it now mounts BEFORE sign-in. Fetching
  // then would fire a guaranteed-401 request on every cold load, so the fetch
  // waits for an account rather than the provider waiting for one.
  const load = useCallback(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    api
      .portfolio()
      .then(setData)
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError ? err : new ApiError(0, err instanceof Error ? err.message : String(err)),
        );
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  useEffect(load, [load]);

  return <Ctx.Provider value={{ data, loading, error, reload: load }}>{children}</Ctx.Provider>;
}

export function usePortfolio(): PortfolioState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePortfolio must be used inside PortfolioProvider');
  return ctx;
}
